/**
 * @file "Can this listener actually hear that speaker?" (#2223 AC44–AC46),
 * kept free of LiveKit and React so it can be tested on its own.
 * `VoicePathMonitor` feeds it one sample per second; it answers with
 * "not audible" stretches (uploaded with the outages, recorded on the
 * timeline, named in receipts) and drives the listener's recovery ladder.
 *
 * Design context:
 * - **Why this exists.** `playing` (voicePath.ts) only says the browser is
 *   *allowed* to play, and the inbound outage only says packets arrive. On
 *   2026-11-04 a listener heard nothing while both said fine, so the report
 *   claimed he heard everyone. "Audible" closes that gap: the speaker is
 *   talking AND what this device actually plays for them is above a noise
 *   floor.
 * - **Talking** comes from LiveKit's server-computed `isSpeaking`, which every
 *   subscriber already receives (no change to the speakers' app, no extra data
 *   message). Judged conservatively: a second counts as talking only if every
 *   reading in it said speaking, so the tail of a sentence never starts a
 *   false stretch.
 * - **Level** is what is played: an analyser on the track's own gain node in
 *   the call's mix context (lib/playbackLevel.ts), so a suspended context, a
 *   dead track or a gain of 0 all read as silence. Max over the second, so a
 *   pause between words does not read as silence.
 * - **Not audible** = talking + level below AUDIBLE_FLOOR for
 *   NOT_AUDIBLE_AFTER_MS; it starts where that quiet run started and ends when
 *   the speaker is audible again, stops talking, mutes or leaves, or the level
 *   can no longer be measured (the user's own volume at 0 is a choice, not a
 *   fault, and `playing` already reports it).
 * - **Ladder.** One per episode (any open stretch), from RECOVER_AFTER_MS
 *   after the oldest stretch began: resume audio → resubscribe → rejoin, each
 *   with a short chance, so a rejoin lands by ~7.5 s and audio is back inside
 *   AC46's 10 s. A new ladder waits LADDER_COOLDOWN_MS after the last one
 *   started; rejoins are additionally gated across ladders by rejoinGate.ts.
 */
import { FIRST_PACKET_GRACE_MS, type OutageEvent } from "./outageTracker";

/** Played level (0-1, same analyser scale as the mic) below which nothing audible plays. */
export const AUDIBLE_FLOOR = 0.01;
/** Talking but inaudible this long is a "couldn't hear" stretch (AC44). */
export const NOT_AUDIBLE_AFTER_MS = 3000;
/** The ladder starts this long after the stretch began (AC46). */
export const RECOVER_AFTER_MS = 5000;
/** Step 1 (resume audio) gets this long before step 2. */
export const RESUME_CHANCE_MS = 1000;
/** Step 2 (resubscribe) gets this long before step 3 (rejoin). */
export const RESUBSCRIBE_CHANCE_MS = 1500;
/** A new ladder may start only this long after the previous one started. */
export const LADDER_COOLDOWN_MS = 30_000;

export interface AudibleSpeakerSample {
  identity: string;
  /** The server said this speaker was speaking throughout the second. */
  talking: boolean;
  /** Highest played level for them this second; null = cannot be measured. */
  level: number | null;
}

export interface AudibleSample {
  at: number;
  connected: boolean;
  speakers: AudibleSpeakerSample[];
}

export interface AudibleTransition {
  speaker: string;
  state: "start" | "end";
  /** When the stretch began (start) or ended (end). */
  at: number;
}

interface SpeakerState {
  firstSeenAt: number;
  quietSince: number | null;
  openId: string | null;
}

function defaultNewId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Per-speaker "couldn't hear" stretches for this listener. */
export class AudibilityTracker {
  private speakers = new Map<string, SpeakerState>();
  private events: OutageEvent[] = [];
  private byId = new Map<string, OutageEvent>();

  constructor(private readonly newId: () => string = defaultNewId) {}

  /** One sample a second; returns the stretches that started or ended. */
  observe(sample: AudibleSample): AudibleTransition[] {
    const out: AudibleTransition[] = [];
    const { at } = sample;
    if (!sample.connected) {
      // Our own link is down: nothing is judged, and nothing stays open.
      for (const [id, s] of this.speakers) this.close(id, s, at, out);
      this.speakers.clear();
      return out;
    }
    const seen = new Set<string>();
    for (const sp of sample.speakers) {
      seen.add(sp.identity);
      let s = this.speakers.get(sp.identity);
      if (!s) {
        s = { firstSeenAt: at, quietSince: null, openId: null };
        this.speakers.set(sp.identity, s);
      }
      // A subscription still completing is not a fault (same grace as inbound outages).
      if (at - s.firstSeenAt < FIRST_PACKET_GRACE_MS) continue;
      if (!sp.talking || sp.level === null) {
        // Stopped talking, or no longer measurable: nothing to claim either way.
        s.quietSince = null;
        this.close(sp.identity, s, at, out);
        continue;
      }
      if (sp.level >= AUDIBLE_FLOOR) {
        s.quietSince = null;
        this.close(sp.identity, s, at, out);
        continue;
      }
      if (s.quietSince === null) s.quietSince = at;
      if (!s.openId && at - s.quietSince >= NOT_AUDIBLE_AFTER_MS) {
        const e: OutageEvent = {
          id: this.newId(),
          kind: "not_audible",
          peer_identity: sp.identity,
          started_at: s.quietSince,
          ended_at: null,
        };
        this.events.push(e);
        this.byId.set(e.id, e);
        s.openId = e.id;
        out.push({ speaker: sp.identity, state: "start", at: s.quietSince });
      }
    }
    // Muted, unpublished, an agent, or gone: end and forget.
    for (const [id, s] of this.speakers) {
      if (seen.has(id)) continue;
      this.close(id, s, at, out);
      this.speakers.delete(id);
    }
    return out;
  }

  /** The participation is ending: close everything at `at`. */
  finish(at: number): AudibleTransition[] {
    const out: AudibleTransition[] = [];
    for (const [id, s] of this.speakers) this.close(id, s, at, out);
    return out;
  }

  /** Speakers this listener cannot hear right now (open stretches). */
  notAudible(): string[] {
    const out: string[] = [];
    for (const [id, s] of this.speakers) if (s.openId) out.push(id);
    return out;
  }

  /** Start of the oldest open stretch, or null when every talking speaker is audible. */
  oldestOpenSince(): number | null {
    let oldest: number | null = null;
    for (const s of this.speakers.values()) {
      if (!s.openId) continue;
      const e = this.byId.get(s.openId);
      if (e && (oldest === null || e.started_at < oldest)) oldest = e.started_at;
    }
    return oldest;
  }

  /** Every stretch so far, as outage events for the call-quality upload. Copies. */
  snapshot(): OutageEvent[] {
    return this.events.map((e) => ({ ...e }));
  }

  private close(id: string, s: SpeakerState, at: number, out: AudibleTransition[]): void {
    if (!s.openId) return;
    const e = this.byId.get(s.openId);
    if (e && e.ended_at === null) e.ended_at = Math.max(at, e.started_at);
    s.openId = null;
    out.push({ speaker: id, state: "end", at });
  }
}

// ── Recovery ladder (AC46) ─────────────────────────────────────────────────

export type AudibleStep = "idle" | "resuming" | "resubscribing" | "rejoining" | "done";
export type AudibleAction = "resumeAudio" | "resubscribe" | "rejoin";

export interface AudibleLadderInput {
  at: number;
  /** AudibilityTracker.oldestOpenSince(). */
  since: number | null;
}

/**
 * resume audio → resubscribe → rejoin, once per episode. Pure: the caller
 * runs the actions (and may find a rejoin refused, e.g. another ladder just
 * rejoined -- the ladder is then simply done).
 */
export class AudibleRecoveryLadder {
  private step: AudibleStep = "idle";
  private stepAt = 0;
  private lastStartAt: number | null = null;

  get current(): AudibleStep {
    return this.step;
  }

  update({ at, since }: AudibleLadderInput): AudibleAction | null {
    if (since === null) {
      // Audible again (or nothing left to judge): the episode is over.
      this.step = "idle";
      return null;
    }
    switch (this.step) {
      case "idle":
        if (at - since < RECOVER_AFTER_MS) return null;
        if (this.lastStartAt !== null && at - this.lastStartAt < LADDER_COOLDOWN_MS) return null;
        this.lastStartAt = at;
        return this.go("resuming", at, "resumeAudio");
      case "resuming":
        return at - this.stepAt < RESUME_CHANCE_MS ? null : this.go("resubscribing", at, "resubscribe");
      case "resubscribing":
        return at - this.stepAt < RESUBSCRIBE_CHANCE_MS ? null : this.go("rejoining", at, "rejoin");
      case "rejoining":
      case "done":
        // One ladder per episode: a rejoin (or a refused one) is the last word.
        this.step = "done";
        return null;
    }
  }

  private go(step: AudibleStep, at: number, action: AudibleAction): AudibleAction {
    this.step = step;
    this.stepAt = at;
    return action;
  }
}
